<script lang="ts">
  import HCaptcha from "./HCaptcha.svelte";
  import Image from "$components/+shared/Image/Image.svelte";
  import Separator from "$components/+shared/Separator/Separator.svelte";
  import Button from "$components/+design-system/Button/Button.svelte";
  import AuthButton from "./AuthButton.svelte";
  import ovDarkLogo from "$assets/images/logos/Ov-dark-logo.svg?url";
  import googleLogo from "$assets/images/logos/Google.svg?url";
  import githubLogo from "$assets/images/logos/Github-mark-white.svg?url";
  import HoneypotField from "./HoneypotField.svelte";
  import { firebaseAuth } from "$utils/firebase/client";
  import { alerts } from "$stores/alert";
  import { community } from "$stores/community";
  import { createEventDispatcher } from "svelte";
  import { fade } from "svelte/transition";
  import {
    getAdditionalUserInfo,
    GithubAuthProvider,
    GoogleAuthProvider,
    signInWithPopup,
    type AuthProvider,
    type UserCredential,
  } from "firebase/auth";
  import { trackNotAuthed } from "app/utils/analytics";
  import { goto } from "$app/navigation";
  import { PUBLIC_HCAPTCHA_SITEKEY } from "$env/static/public";

  export let heading = "";
  export let buttonText = "";
  export let email = "";
  export let emailInputDisabled = false;
  export let emailPlaceholder: string;
  export let passwordPlaceholder = "Password";
  export let error = ""; // temporary to support Reauthenticate.tsx
  export let hasBackground = true;
  export let showOutverseLogo = true;
  export let showResetPassword = false;
  export let isSignUp = false;
  export let submitting = false;
  let honeypot: HoneypotField;

  const dispatch = createEventDispatcher<{
    authed: { provider: string; user: UserCredential; isNewUser: boolean };
    submit: { email: string; password: string; captcha: string };
    reset: { email: string };
  }>();

  const googleProvider = new GoogleAuthProvider();
  const githubProvider = new GithubAuthProvider();
  let captcha: HCaptcha;
  let form: HTMLFormElement;
  let captchaToken = "";

  function handleSubmit(e: SubmitEvent) {
    if (submitting) return;
    submitting = true;

    try {
      const data = new FormData(e.target as HTMLFormElement);

      // check the honeypot field
      // silently reject if field is filled
      if (honeypot.isFilled()) {
        submitting = false;

        // log honeypot detection
        trackNotAuthed(
          "hp_detected",
          data.get("email")?.toString() || "",
          honeypot.content() || ""
        );

        // redirect to fake verify page
        goto("/sign-up/hp-verify");
        return;
      }

      dispatch("submit", {
        email: data.get("email") as string,
        password: data.get("password") as string,
        captcha: captchaToken,
      });
    } catch (error) {
      alerts.error(error as string);
      submitting = false;
    }
  }

  async function signInWithPopupHandler(provider: AuthProvider) {
    try {
      const result = await signInWithPopup(firebaseAuth, provider);
      const info = getAdditionalUserInfo(result);
      dispatch("authed", {
        provider: provider.providerId,
        user: result,
        isNewUser: !!info?.isNewUser,
      });
    } catch (error: any) {
      if (error.code === "auth/cancelled-popup-request") {
        // no need to report this error
        return;
      }
      throw error;
    }
  }

  function handleCaptchaError() {
    captcha.reset();
    alerts.error("Failed to verify user");
  }
  function handleCaptchaSuccess(ev: { detail: { token: string } }) {
    captchaToken = ev.detail.token;
    form.requestSubmit();
  }

  $: if (error) submitting = false;
</script>

<!-- we add the dark theme class here to override the theme for just this component which only has dark styling  -->
<div
  in:fade|global
  class="dark w-full max-w-[400px] rounded-xl px-8 py-5 text-center {hasBackground
    ? 'bg-gray-700 bg-opacity-75 brightness-[1.15] backdrop-blur-[26px] backdrop-brightness-110 backdrop-contrast-100 backdrop-saturate-[1.2]'
    : ''}"
>
  {#if showOutverseLogo}
    <Image fallback src={ovDarkLogo} class="m-auto mb-2 h-10" alt="Outverse" />
  {:else if $community.fetched && $community.logoImage}
    <Image
      src={$community.logoImage}
      class="m-auto mb-2 h-14 rounded-md"
      alt={$community.name || ""}
    />
  {/if}

  <div class="mt-4 text-lg font-medium text-gray-300">{heading}</div>
  <slot name="subheading" />

  <div class="mx-auto mt-8 space-y-3 text-left">
    <form bind:this={form} name="authentication" on:submit|preventDefault={handleSubmit}>
      <div class="mt-4 w-full">
        <slot name="firebaseUI">
          <div class="flex flex-col gap-5">
            <AuthButton
              on:click={() => firebaseAuth && signInWithPopupHandler(googleProvider)}
              logo={googleLogo}
              text="Continue with Google"
            />
            <AuthButton
              on:click={() => firebaseAuth && signInWithPopupHandler(githubProvider)}
              logo={githubLogo}
              text="Continue with GitHub"
            />
          </div>
          <Separator class="my-8 bg-white/10" />
        </slot>
      </div>
      <div class="flex flex-col items-center gap-5">
        <label for="email" class="sr-only">Email</label>
        <!-- email -->
        <input
          class="block w-full rounded-lg border border-white/10 bg-transparent px-3 py-2 text-sm text-gray-400 outline-none transition-colors placeholder:text-gray-400/60 focus:border-[#6776FF]"
          name="email"
          placeholder={emailPlaceholder}
          bind:value={email}
          autocomplete="email"
          disabled={emailInputDisabled}
        />
        <!-- password -->
        <label for="password" class="sr-only">Password - 8 character minimum</label>
        <input
          class="block w-full rounded-lg border border-white/10 bg-transparent px-3 py-2 text-sm text-gray-400 transition-colors placeholder:text-gray-400/60 focus:border-[#6776FF]"
          name="password"
          type="password"
          autocomplete={isSignUp ? "new-password" : "current-password"}
          placeholder={passwordPlaceholder}
        />

        <!-- honeypot -->
        <HoneypotField bind:this={honeypot} />
        {#if isSignUp}
          <HCaptcha
            bind:this={captcha}
            sitekey={PUBLIC_HCAPTCHA_SITEKEY}
            on:error={handleCaptchaError}
            on:success={handleCaptchaSuccess}
            size="invisible"
          />
        {/if}
        <Button
          label={submitting ? "Submitting..." : buttonText}
          htmlButtonType="button"
          on:click={() => (isSignUp ? captcha.execute(null) : form.requestSubmit())}
          variant="primary"
          size="large"
          isFullWidth
        />

        {#if showResetPassword}
          <span class="flex w-full justify-center">
            <Button
              label="Reset password"
              htmlButtonType="button"
              variant="tertiary"
              size="small"
              on:click={() => {
                if (email) {
                  dispatch("reset", { email });
                } else {
                  alerts.error("Please enter an email to send reset link to");
                }
              }}
            />
          </span>
        {/if}
      </div>
    </form>

    <div class="text-center text-sm text-red-500">
      <slot name="error">{error}</slot>
    </div>
  </div>

  <slot name="footer" />
</div>
